import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { communicationEquipmentApi } from '../../api/communicationEquipment';
import { downloadBlobAsFile } from '../../utils/download';

function Stat({ label, value, tone = 'default' }) {
  const colors = {
    default: { bg: '#F7F9FC', color: '#1F3550' },
    ok: { bg: '#EAF8F1', color: '#0F9F6E' },
    warn: { bg: '#FFF4E5', color: '#B45309' },
    danger: { bg: '#FEF2F2', color: '#B91C1C' },
  }[tone];
  return (
    <Box sx={{ px: 1.2, py: 1, borderRadius: 2.5, background: colors.bg, minWidth: 108 }}>
      <Typography sx={{ fontSize: 11, color: '#64748B' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 900, color: colors.color, fontSize: 18, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  );
}

export default function PulseRegistrarImportDialog({ open, buildingId, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError('');
    setDone(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const problemRows = useMemo(
    () => (preview?.rows || []).filter((row) => row.errors?.length || row.warnings?.length).slice(0, 12),
    [preview]
  );

  const handleTemplate = async () => {
    try {
      setError('');
      const blob = await communicationEquipmentApi.downloadImportTemplate(buildingId);
      downloadBlobAsFile(blob, 'pulse_registrars_template.xlsx');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось скачать шаблон.');
    }
  };

  const handlePreview = async (selectedFile) => {
    if (!selectedFile) return;
    setLoading(true);
    setError('');
    setDone(false);
    try {
      const result = await communicationEquipmentApi.previewImport(buildingId, selectedFile);
      setFile(selectedFile);
      setPreview(result);
    } catch (err) {
      setPreview(null);
      setError(err?.response?.data?.detail || 'Не удалось проверить файл.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !preview?.ok || loading) return;
    setLoading(true);
    setError('');
    try {
      const result = await communicationEquipmentApi.runImport(buildingId, file);
      setPreview(result);
      if (result.ok) {
        setDone(true);
        onImported?.(result);
      } else {
        setError('Импорт остановлен: в файле остались ошибки.');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось выполнить импорт.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 900, color: '#1F3550', fontSize: 20, lineHeight: 1.2 }}>
          Импорт из Excel
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Сначала проверим файл, затем запишем привязки в дом. Повторная загрузка не создаёт дубли.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#F7F9FC' }}>
        <Stack spacing={1.5}>
          {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
          {done ? (
            <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ borderRadius: 2.5 }}>
              Импорт выполнен. Карта каналов обновлена.
            </Alert>
          ) : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: '1px solid rgba(38, 65, 94, 0.12)',
              background: '#fff',
              display: 'grid',
              gap: 1.25,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box>
                <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>Файл Excel</Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  В каждой строке — один счётчик и его канал. Один модуль может занимать несколько строк.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleTemplate}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
              >
                Скачать шаблон
              </Button>
            </Stack>

            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              disabled={loading}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                fontWeight: 800,
                justifyContent: 'flex-start',
                py: 1.4,
                borderStyle: 'dashed',
              }}
            >
              {file ? file.name : 'Выбрать файл .xlsx'}
              <input
                hidden
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => handlePreview(event.target.files?.[0])}
              />
            </Button>
          </Box>

          {loading && !preview ? (
            <Box sx={{ minHeight: 120, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {preview ? (
            <Box sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(38, 65, 94, 0.12)', background: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>
                  {preview.ok ? 'Файл готов к импорту' : 'Нужно исправить ошибки'}
                </Typography>
                <Chip
                  size="small"
                  color={preview.ok ? 'success' : 'error'}
                  label={preview.ok ? 'Проверка пройдена' : `${preview.error_count} ошибок`}
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.2 }}>
                <Stat label="Новых модулей" value={preview.registrars_to_create} tone="ok" />
                <Stat label="Уже в доме" value={preview.registrars_found} />
                <Stat label="Новых счётчиков" value={preview.devices_to_create} tone="ok" />
                <Stat label="Новых привязок" value={preview.bindings_to_create} tone="ok" />
                <Stat label="Обновлений" value={preview.bindings_to_update} />
                <Stat label="Свободных каналов" value={preview.empty_channels_after} tone="warn" />
              </Stack>

              {problemRows.length ? (
                <Stack spacing={0.7}>
                  {problemRows.map((row) => (
                    <Box
                      key={`${row.row_number}-${row.device_serial}`}
                      sx={{
                        px: 1.1,
                        py: 0.85,
                        borderRadius: 2,
                        background: row.errors?.length ? '#FEF2F2' : '#FFF9F0',
                        border: `1px solid ${row.errors?.length ? 'rgba(185,28,28,0.18)' : 'rgba(180,83,9,0.18)'}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1F3550' }}>
                        Строка {row.row_number}
                        {row.apartment_number ? ` · кв. ${row.apartment_number}` : ''}
                        {row.channel_number ? ` · канал ${row.channel_number}` : ''}
                      </Typography>
                      {(row.errors || []).map((item) => (
                        <Typography key={item} sx={{ fontSize: 12, color: '#B91C1C' }}>{item}</Typography>
                      ))}
                      {(row.warnings || []).map((item) => (
                        <Typography key={item} sx={{ fontSize: 12, color: '#B45309' }}>{item}</Typography>
                      ))}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Alert severity="success" sx={{ borderRadius: 2.5 }}>
                  Ошибок нет. Можно подтверждать импорт.
                </Alert>
              )}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={handleClose} disabled={loading}>Закрыть</Button>
        <Button
          variant="contained"
          disabled={!preview?.ok || loading || done}
          onClick={handleImport}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 850, boxShadow: 'none' }}
        >
          {loading ? 'Импорт…' : 'Импортировать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
